import { axiosInstance } from "../axios-Instance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../react-query/constants";
import { toast } from "react-toastify";
import { toastOptions } from "../utils";
import { getLoginToken } from "../storage";
import { BatchAssetPayload } from "../modules/admin/Settings/Asset";

async function getAsset() {
  const data = await axiosInstance({
    url: "/asset",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

const createAsset = async (formData: any) => {
  const data = await axiosInstance({
    url: "/asset",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

type CreateAssetsBatchArgs = {
  roundId: string;
  assets: BatchAssetPayload[];
};

export const createAssetsBatch = async ({
  roundId,
  assets,
}: CreateAssetsBatchArgs) => {
  const fd = new FormData();
  fd.append("roundId", roundId);
  fd.append("assets", JSON.stringify(assets));

  const { data } = await axiosInstance({
    url: "/asset/batch",
    method: "POST",
    data: fd,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data;
};

const updateAsset = async (formData: any) => {
  const data = await axiosInstance({
    url: `/asset/${formData["_id"]}`,
    method: "PUT",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const deleteAsset = async (formData: any) => {
  const data = await axiosInstance({
    url: `/asset/${formData}`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

export function useAsset() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.asset],
    queryFn: () => getAsset(),
  });
  return data;
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => createAsset(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.asset]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useCreateAssetBatch() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (payload: CreateAssetsBatchArgs) => createAssetsBatch(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.asset]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => updateAsset(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.asset]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => deleteAsset(formData),
    onSuccess: (data) => {
      toast.success("Asset Deleted Successfully", toastOptions);
      queryClient.invalidateQueries([queryKeys.asset]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}
